import React from 'react';
import { Text, useTheme, IconButton, IContextualMenuProps } from '@fluentui/react';
import {
    RegionalPipelineData,
    Pipeline,
} from '../../models/CloudStatusModels';
import {
    StageCircle,
    ConnectionLine,
    getPipelineName,
    getWorkLaneName,
    getRegionName,
} from '../../utils/StatusHelpers';

interface PipelinesTabProps {
    pipelines: RegionalPipelineData[];
}

export const PipelinesTab: React.FC<PipelinesTabProps> = ({ pipelines }) => {
    const theme = useTheme();

    const dataPipelines = pipelines.filter((p) => p.pipeline === Pipeline.Blob);
    const dialtonePipelines = pipelines.filter((p) => p.pipeline === Pipeline.Core);
    const messagePipelines = pipelines.filter((p) => p.pipeline === Pipeline.Message);

    const handleSetActive = (pipeline: RegionalPipelineData) => {
        console.log('Set Active clicked for pipeline:', pipeline);
        // TODO: Implement set active functionality
    };

    const getAdminMenuProps = (pipeline: RegionalPipelineData): IContextualMenuProps => ({
        items: [
            {
                key: 'setActive',
                text: 'Set Active',
                iconProps: { iconName: 'CheckMark' },
                onClick: () => handleSetActive(pipeline),
            },
        ],
    });

    const renderPipelineCard = (pipeline: RegionalPipelineData, index: number) => (
        <div
            key={`pipeline-${index}`}
            style={{
                backgroundColor: theme.palette.neutralTertiaryAlt,
                border: `1px solid ${theme.palette.neutralTertiary}`,
                borderRadius: '4px',
                padding: '20px',
                marginBottom: '16px',
                boxShadow: '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                position: 'relative',
                marginRight: '15px'
            }}
        >
            {/* Admin Button */}
            <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
                <IconButton
                    menuProps={getAdminMenuProps(pipeline)}
                    iconProps={{ iconName: 'Settings' }}
                    title="Admin Options"
                    ariaLabel="Admin Options"
                    styles={{
                        root: {
                            width: '32px',
                            height: '32px',
                            backgroundColor: theme.palette.neutralLighter,
                            borderRadius: '4px',
                        },
                        rootHovered: {
                            backgroundColor: theme.palette.neutralLight,
                        },
                    }}
                />
            </div>

            {/* Header Section */}
            <div style={{ marginBottom: '16px', paddingRight: '40px' }}>
                <Text
                    variant="xLarge"
                    styles={{
                        root: {
                            fontWeight: 600,
                            color: theme.palette.black,
                            display: 'block',
                            marginBottom: '4px',
                        },
                    }}
                >
                    {getPipelineName(pipeline.pipeline)} Pipeline
                </Text>
                <Text
                    variant="small"
                    styles={{
                        root: {
                            color: theme.palette.neutralTertiary,
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            display: 'block',
                            marginBottom: '8px',
                        },
                    }}
                >
                    Flow ID: {pipeline.flowId}
                </Text>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <Text variant="medium" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                        <strong>Work Lane:</strong> {getWorkLaneName(pipeline.workLane)}
                    </Text>
                    <Text variant="medium" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                        <strong>Region:</strong> {getRegionName(pipeline.region)}
                    </Text>
                    <Text variant="medium" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                        <strong>Direction:</strong> {pipeline.direction || 'N/A'}
                    </Text>
                </div>
            </div>

            {/* Regions Section */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: theme.palette.neutralLighterAlt,
                    borderRadius: '4px',
                }}
            >
                <div>
                    <Text
                        variant="medium"
                        styles={{ root: { fontWeight: 600, marginBottom: '4px', display: 'block' } }}
                    >
                        Public Region
                    </Text>
                    <Text variant="medium">{pipeline.primaryRegion || 'N/A'}</Text>
                </div>
                <div>
                    <Text
                        variant="medium"
                        styles={{ root: { fontWeight: 600, marginBottom: '4px', display: 'block' } }}
                    >
                        Cloud Region
                    </Text>
                    <Text variant="medium">{pipeline.secondaryRegion || 'N/A'}</Text>
                </div>
            </div>

            {/* Source and Destination Section */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '16px',
                    marginBottom: '20px',
                }}
            >
                <div>
                    <Text
                        variant="medium"
                        styles={{ root: { fontWeight: 600, marginBottom: '8px', display: 'block' } }}
                    >
                        Source
                    </Text>
                    <div
                        style={{
                            padding: '8px',
                            backgroundColor: theme.palette.neutralLighter,
                            borderRadius: '4px',
                            marginBottom: '4px',
                        }}
                    >
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    wordBreak: 'break-all',
                                    display: 'block',
                                },
                            }}
                        >
                            {pipeline.source || 'N/A'}
                        </Text>
                    </div>

                    <Text
                        variant="medium"
                        styles={{ root: { fontWeight: 600, marginBottom: '8px', display: 'block' } }}
                    >
                        Source Region
                    </Text>
                    <div
                        style={{
                            padding: '8px',
                            backgroundColor: theme.palette.neutralLighter,
                            borderRadius: '4px',
                            marginBottom: '4px',
                        }}
                    >
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    wordBreak: 'break-all',
                                    display: 'block',
                                },
                            }}
                        >
                            {pipeline.sourceRegion || 'N/A'}
                        </Text>
                    </div>

                </div>
                <div>
                    <Text
                        variant="medium"
                        styles={{ root: { fontWeight: 600, marginBottom: '8px', display: 'block' } }}
                    >
                        Destination
                    </Text>
                    <div
                        style={{
                            padding: '8px',
                            backgroundColor: theme.palette.neutralLighter,
                            borderRadius: '4px',
                            marginBottom: '4px',
                        }}
                    >
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    wordBreak: 'break-all',
                                    display: 'block',
                                },
                            }}
                        >
                            {pipeline.destination || 'N/A'}
                        </Text>
                    </div>

                    <Text
                        variant="medium"
                        styles={{ root: { fontWeight: 600, marginBottom: '8px', display: 'block' } }}
                    >
                        Destination Region
                    </Text>
                    <div
                        style={{
                            padding: '8px',
                            backgroundColor: theme.palette.neutralLighter,
                            borderRadius: '4px',
                            marginBottom: '4px',
                        }}
                    >
                        <Text
                            variant="small"
                            styles={{
                                root: {
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    wordBreak: 'break-all',
                                    display: 'block',
                                },
                            }}
                        >
                            {pipeline.destinationRegion || 'N/A'}
                        </Text>
                    </div>

                </div>
            </div>

            {/* Pipeline Status Section */}
            <div>
                <Text
                    variant="medium"
                    styles={{ root: { fontWeight: 600, marginBottom: '12px', display: 'block' } }}
                >
                    Pipeline Status
                </Text>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '16px',
                        backgroundColor: theme.palette.neutralLighter,
                        borderRadius: '4px',
                        overflowX: 'auto',
                    }}
                >
                    <StageCircle stage={pipeline.stages?.public} />
                    <ConnectionLine connection={pipeline.connections?.publicToPublicTransfer} />
                    <StageCircle stage={pipeline.stages?.publicTransfer} />
                    <ConnectionLine connection={pipeline.connections?.publicTransferToCloudArrive} />
                    <StageCircle stage={pipeline.stages?.cloudArrive} />
                    <ConnectionLine connection={pipeline.connections?.cloudArriveToCloudEngine} />
                    <StageCircle stage={pipeline.stages?.cloudEngine} />
                    <div style={{ width: '12px' }} />
                    <StageCircle stage={pipeline.stages?.cloudPlatform} />
                </div>
            </div>
        </div>
    );

    return (
        <div
            style={{
                paddingTop: '16px',
                width: '100%',
                padding: '16px',
            }}
        >
            {/* Data Pipelines Section */}
            {dataPipelines.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <Text
                        variant="xLarge"
                        styles={{
                            root: {
                                fontWeight: 600,
                                marginBottom: '16px',
                                display: 'block',
                                color: theme.palette.themePrimary,
                            },
                        }}
                    >
                        Data Pipelines
                    </Text>
                    {dataPipelines.map((pipeline, index) => renderPipelineCard(pipeline, index))}
                </div>
            )}

            {/* Dialtone Pipelines Section */}
            {dialtonePipelines.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <Text
                        variant="xLarge"
                        styles={{
                            root: {
                                fontWeight: 600,
                                marginBottom: '16px',
                                display: 'block',
                                color: theme.palette.themePrimary,
                            },
                        }}
                    >
                        Dialtone Pipelines
                    </Text>
                    {dialtonePipelines.map((pipeline, index) => renderPipelineCard(pipeline, index))}
                </div>
            )}

            {/* Message Pipelines Section */}
            {messagePipelines.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <Text
                        variant="xLarge"
                        styles={{
                            root: {
                                fontWeight: 600,
                                marginBottom: '16px',
                                display: 'block',
                                color: theme.palette.themePrimary,
                            },
                        }}
                    >
                        Message Pipelines
                    </Text>
                    {messagePipelines.map((pipeline, index) => renderPipelineCard(pipeline, index))}
                </div>
            )}

            {/* No Data Message */}
            {dataPipelines.length === 0 &&
                dialtonePipelines.length === 0 &&
                messagePipelines.length === 0 && (
                    <div
                        style={{
                            padding: '48px 24px',
                            textAlign: 'center',
                            backgroundColor: theme.palette.neutralLighterAlt,
                            borderRadius: '4px',
                        }}
                    >
                        <Text
                            variant="large"
                            styles={{
                                root: {
                                    color: theme.palette.neutralSecondary,
                                    fontStyle: 'italic',
                                },
                            }}
                        >
                            No pipeline data available. Check browser console for debugging
                            information.
                        </Text>
                    </div>
                )}
        </div>
    );
};
